// src/pages/Dashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard, clearStudyPlan } from "../api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  const name = localStorage.getItem("userName") || "Student";
  const exam = localStorage.getItem("targetExam") || "IBPS PO";

  useEffect(() => {
    fetchDashboard()
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const handleClearPath = async () => {
    if (!window.confirm("Are you sure you want to clear your current study path? Your progress on this map will be lost.")) return;
    try {
      await clearStudyPlan();
      // Refresh dashboard data
      setLoading(true);
      const d = await fetchDashboard();
      setData(d);
      setLoading(false);
    } catch (err) {
      alert("Error clearing path: " + err.message);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading dashboard…</span></div>;

  if (error && !data) {
    return (
      <div className="container" style={{ padding: 40, textAlign: "center" }}>
        <div className="error-box">{error}</div>
        <button className="btn btn-blue" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!data) return null;

  // If no data yet (new user), show empty state
  const overall      = data.overall || {};
  const weakTopics   = data.weakTopics || [];
  const strongTopics = data.strongTopics || [];
  const recentTests  = data.recentTests || [];

  const overallAcc      = Math.round((overall.accuracy || 0) * 100);
  const questionsAttempted = overall.questionsAttempted || 0;
  const correctAnswers  = overall.correctAnswers || 0;
  const testsCount      = overall.testsCount || 0;

  return (
    <div>
      {/* AI Planner Banner */}
      {!data.studyPlan ? (
        <div className="banner" style={{ background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)", marginBottom: 24 }}>
          <div>
            <div className="banner-title" style={{ color: "#fff" }}>Goal-Oriented AI Planning</div>
            <p className="banner-desc" style={{ color: "rgba(255,255,255,0.9)" }}>
              No study map? Tell AI your target exam and timeframe. 
              We'll build a structured path and ready the questions for you.
            </p>
          </div>
          <button className="btn" style={{ background: "#fff", color: "#6366f1", fontWeight: 700 }} onClick={() => navigate("/planner")}>
            Plan Now
          </button>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 24, borderLeft: "4px solid var(--blue)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--blue)", marginBottom: 4 }}>ACTIVE STUDY PATH</div>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>{data.activeExamGoal} Prep &middot; {data.studyPlan.timeframe}</h2>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-blue btn-sm" onClick={() => navigate("/planner")}>View Full Map</button>
              <button className="btn btn-sm" onClick={handleClearPath} style={{ color: "var(--red)", border: "1px solid var(--red-light)" }}>🗑️ Clear Path</button>
            </div>
          </div>
        </div>
      )}

      {/* Continue banner */}

      {/* Stats row */}
      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-val" style={{ color: "var(--blue)" }}>{overallAcc}%</div>
          <div className="stat-lbl">Overall accuracy</div>
        </div>
        <div className="stat-box">
          <div className="stat-val">{questionsAttempted}</div>
          <div className="stat-lbl">Questions done</div>
        </div>
        <div className="stat-box">
          <div className="stat-val" style={{ color: "var(--green)" }}>{correctAnswers}</div>
          <div className="stat-lbl">Correct answers</div>
        </div>
        <div className="stat-box">
          <div className="stat-val">{testsCount}</div>
          <div className="stat-lbl">Tests taken</div>
        </div>
      </div>

      {/* Weak + Strong topic cards */}
      <div className="two-col">
        {/* Weak topics */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--red)" }}>Weak topics</span>
            <span className="badge badge-weak">{weakTopics.length} topics</span>
          </div>
          {weakTopics.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              No weak topics yet — take a test to get started!
            </p>
          ) : (
            weakTopics.map((t, i) => (
              <div className="topic-row" key={i}>
                <span>{t.topic}</span>
                <span style={{ color: "var(--red)", fontWeight: 600 }}>
                  {Math.round(t.accuracy * 100)}%
                </span>
              </div>
            ))
          )}
          {weakTopics.length > 0 && (
            <button
              className="btn"
              style={{ width: "100%", marginTop: 14, borderRadius: 10 }}
              onClick={() => navigate("/practice")}
            >
              Practice weak topics
            </button>
          )}
        </div>

        {/* Strong topics */}
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--green)" }}>Strong topics</span>
            <span className="badge badge-strong">{strongTopics.length} topics</span>
          </div>
          {strongTopics.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Keep practising to build your strong topics!
            </p>
          ) : (
            strongTopics.map((t, i) => (
              <div className="topic-row" key={i}>
                <span>{t.topic}</span>
                <span style={{ color: "var(--green)", fontWeight: 600 }}>
                  {Math.round(t.accuracy * 100)}%
                </span>
              </div>
            ))
          )}
          {strongTopics.length > 0 && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10 }}>
              Keep challenging yourself with hard questions
            </p>
          )}
        </div>
      </div>

      {/* Recent tests */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>Recent tests</span>
          <span className="badge" style={{ background: "var(--bg)", color: "var(--text-secondary)" }}>
            Last 5
          </span>
        </div>
        {recentTests.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
              No tests taken yet. Start your first test!
            </p>
            <button className="btn btn-dark btn-sm" onClick={() => navigate("/test")}>
              Take a test
            </button>
          </div>
        ) : (
          recentTests.map((t, i) => (
            <div className="topic-row" key={i}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, textTransform: "capitalize" }}>
                  {t.testType.replace("-", " ")} test
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                  {t.subject || "All subjects"} · {new Date(t.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short" })}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontSize: 13, fontWeight: 600,
                  color: t.accuracy >= 0.7 ? "var(--green)" : t.accuracy >= 0.5 ? "var(--amber)" : "var(--red)"
                }}>
                  {Math.round(t.accuracy * 100)}%
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {t.correct} correct
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
